import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, IconButton, CircularProgress, Snackbar,
  Accordion, AccordionSummary, AccordionDetails,
  Checkbox, FormControlLabel, Divider, Grid,
} from "@mui/material";
import { Alert } from '@mui/material';
import { Close as CloseIcon } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchAccessRights, saveAccessRights } from "../redux/companies.action";

export default function CompanyAccessRightDialog({ selectedCompany, onClose }) {
  const dispatch = useDispatch();
  const { accessRights } = useSelector((state) => state.companies);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (selectedCompany) {
      setIsLoading(true);
      dispatch(fetchAccessRights(selectedCompany.id)).then(() => setIsLoading(false));
    }
  }, [selectedCompany, dispatch]);

  useEffect(() => {
    setSelectedIds([...accessRights.selectedIds]);
  }, [accessRights.selectedIds]);

  const toggleId = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const selectAll = () => {
    const allIds = [];
    accessRights.list.forEach((group) => {
      (group.subgroup || []).forEach((s) => {
        (s.details || []).forEach((d) => allIds.push(d.id));
      });
    });
    setSelectedIds(allIds);
  };

  const deselectAll = () => setSelectedIds([]);

  const handleSave = async () => {
    setIsSubmitting(true);
    const result = await dispatch(saveAccessRights(selectedCompany.id, selectedIds));
    setIsSubmitting(false);
    if (result.success) {
      setSnack({ open: true, message: "Access rights updated!", severity: "success" });
      setTimeout(onClose, 1000);
    } else {
      setSnack({ open: true, message: result.message, severity: "error" });
    }
  };

  return (
    <Dialog open={!!selectedCompany} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography style={{ fontWeight: 700, fontSize: 14, color: "#3A4D54" }}>
            Access Rights - {selectedCompany?.name}
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon style={{ fontSize: 18 }} /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ backgroundColor: "#f3f3f3", maxHeight: "70vh" }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
        ) : (
          <>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button variant="text" size="small" onClick={selectAll} style={{ textTransform: "none", color: "#6AAF68" }}>Select All</Button>
              <Button variant="text" size="small" onClick={deselectAll} style={{ textTransform: "none", color: "red" }}>Deselect All</Button>
            </Box>
            {accessRights.list.map((group, gi) => (
              <Accordion key={gi} style={{ marginBottom: 8 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography style={{ fontWeight: 600 }}>{group.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box width="100%">
                    {(group.subgroup || []).map((subgroup, si) => (
                      <Box key={si} mb={1}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>{subgroup.name}</Typography>
                        <Divider style={{ marginBottom: 8 }} />
                        <Grid container>
                          {(subgroup.details || []).map((detail) => (
                            <Grid item xs={6} sm={4} key={detail.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedIds.includes(detail.id)}
                                    onChange={() => toggleId(detail.id)}
                                    size="small"
                                  />
                                }
                                label={<Typography variant="body2">{detail.action}</Typography>}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: 16 }}>
        <Button variant="outlined" onClick={onClose} style={{ color: "#3A4D54", borderColor: "#3A4D54" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={isSubmitting}
          style={{ backgroundColor: "#3A4D54", color: "#fff" }}>
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.message}</Alert>
      </Snackbar>
    </Dialog>
  );
}
